import { TTypographyLevels } from '@lib/theme/typography.types';
import React from 'react';
import { DefaultTheme, useTheme } from 'styled-components';
import { TButtonVariant, TButtonSize } from './button.types';

export const loaderStyle: Record<TButtonVariant, 'on-dark' | 'on-light'> = {
  primary: 'on-light',
  secondary: 'on-light',
  'primary-dark': 'on-dark',
  danger: 'on-dark',
  ghost: 'on-light',
};

export const buttonSize: Record<
  TButtonSize,
  { height: number; minWidth: number; borderRadius: number }
> = {
  large: { height: 48, minWidth: 48, borderRadius: 12 },
  medium: { height: 40, minWidth: 40, borderRadius: 8 },
  small: { height: 32, minWidth: 32, borderRadius: 8 },
};

export const iconSize: Record<TButtonSize, { width: number; height: number }> = {
  large: { height: 24, width: 24 },
  medium: { height: 20, width: 20 },
  small: { height: 20, width: 20 },
};

export const coreSizes: Record<TButtonSize, { padding: number; component: number }> = {
  large: {
    padding: 24,
    component: 44,
  },
  medium: {
    padding: 16,
    component: 30,
  },
  small: {
    padding: 16,
    component: 44,
  },
};

export const buttonTextConfig: Record<
  TButtonSize,
  { display: TTypographyLevels; variant: 'default' }
> = {
  large: {
    display: 'buttonPrimary',
    variant: 'default',
  },
  medium: {
    display: 'buttonText',
    variant: 'default',
  },
  small: {
    display: 'buttonText',
    variant: 'default',
  },
};

export const overlayConfig: Record<TButtonVariant, 'light' | 'dark'> = {
  primary: 'dark',
  secondary: 'light',
  danger: 'light',
  ghost: 'light',
  'primary-dark': 'dark',
};

type TUseButtonStyle = {
  variant: TButtonVariant;
  size: TButtonSize;
};

export function getButtonColors({
  variant,
  theme,
}: {
  variant: TButtonVariant;
  theme: DefaultTheme;
}) {
  let color;
  let backgroundColor;

  switch (variant) {
    case 'secondary':
      backgroundColor = theme.color.surface.secondary;
      color = theme.color.tint.onPrimary;
      break;
    case 'primary-dark':
      backgroundColor = theme.color.surface.variantDark;
      color = theme.color.tint.primaryDark;
      break;
    case 'danger':
      backgroundColor = theme.color.solid.error;
      color = theme.color.tint.onSolid;
      break;
    case 'ghost':
      backgroundColor = 'transparent';
      color = theme.color.tint.action;
      break;
    default:
      backgroundColor = theme.color.surface.primary;
      color = theme.color.tint.onPrimary;
      break;
  }

  return {
    color,
    backgroundColor,
  };
}

export function useButtonStyle(params: TUseButtonStyle) {
  const theme = useTheme();

  return React.useMemo(() => {
    return {
      ...getButtonColors({ variant: params.variant, theme }),
      textConfig: buttonTextConfig[params.size],
    };
  }, [theme, params.variant, params.size]);
}
